import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Calendar from './components/Calendar';
import HoursSummaryPage from './components/HoursSummaryPage';
import Employees from './components/Employees';
import Requests from './components/Requests';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  // Debug: Confirm this is the MANAGER software
  console.log('🚀🚀🚀 MANAGER SOFTWARE LOADED - URL:', window.location.href);
  console.log('🚀🚀🚀 MANAGER SOFTWARE LOADED - Search params:', window.location.search);
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Helper function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const checkAuth = useCallback(async () => {
    console.log('🔍🔍🔍 MANAGER SOFTWARE: Starting authentication check...');
    setIsLoading(true);
    setHasError(false);
    
    try {
      // Check for token in URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      let token = urlParams.get('token');
      
      console.log('🔍🔍🔍 MANAGER SOFTWARE: URL search params:', window.location.search);
      console.log('🔍🔍🔍 MANAGER SOFTWARE: Token from URL:', token ? 'TOKEN FOUND' : 'NO TOKEN');
      
      if (token) {
        console.log('📝📝📝 MANAGER SOFTWARE: Token found in URL parameters - storing...');
        localStorage.setItem('token', token);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // Check localStorage, sessionStorage, and cookies
        token = localStorage.getItem('token') || 
                sessionStorage.getItem('token') || 
                getCookie('token');
        
        if (token) {
          console.log('📝📝📝 MANAGER SOFTWARE: Token found in storage');
        } else {
          console.log('❌❌❌ MANAGER SOFTWARE: No token found, user needs to authenticate');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
      }

      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
      console.log('🔗🔗🔗 MANAGER SOFTWARE: Using API URL:', API_BASE_URL);

      // Validate token with backend
      console.log('🔐🔐🔐 MANAGER SOFTWARE: Validating token with backend...');
      const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅✅✅ MANAGER SOFTWARE: Token validation successful');
        setIsAuthenticated(true);
        localStorage.setItem('token', token);
      } else if (response.status === 401) {
        console.log('🔄🔄🔄 MANAGER SOFTWARE: Token expired, attempting refresh...');
        // Try to refresh token
        const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          console.log('✅✅✅ MANAGER SOFTWARE: Token refresh successful');
          localStorage.setItem('token', refreshData.token);
          setIsAuthenticated(true);
        } else {
          console.log('❌❌❌ MANAGER SOFTWARE: Token refresh failed');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          setIsAuthenticated(false);
        }
      } else {
        console.log('❌❌❌ MANAGER SOFTWARE: Token validation failed');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('💥💥💥 MANAGER SOFTWARE: Authentication error:', error);
      setHasError(true);
      // Allow offline mode
      setIsAuthenticated(false);
    } finally {
      console.log('🏁🏁🏁 MANAGER SOFTWARE: Authentication check complete, setting loading to false');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for unauthorized events from other components
  useEffect(() => {
    const handleUnauthorized = (event) => {
      console.log('Manager: Received unauthorized event:', event.detail);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setIsAuthenticated(false);
    window.location.href = 'https://calendar.joinoptivo.com';
  };

  // Show loading screen
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2>Loading Manager Dashboard...</h2>
          <p>Please wait while we authenticate your session</p>
        </div>
      </div>
    );
  }

  // Show error screen
  if (hasError) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Connection Error</h2>
          <p>Unable to connect to the server. Please check your internet connection and try again.</p>
          <div className="error-actions">
            <button 
              onClick={() => {
                setHasError(false);
                checkAuth();
              }}
              className="retry-button"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = 'https://calendar.joinoptivo.com'}
              className="calendar-button"
            >
              Go to Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app-container">
        <Routes>
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/calendar" 
            element={
              isAuthenticated ? (
                <Calendar />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/employees" 
            element={
              isAuthenticated ? (
                <Employees />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/requests" 
            element={
              isAuthenticated ? (
                <Requests />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/hours-summary" 
            element={
              isAuthenticated ? (
                <HoursSummaryPage />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <div className="auth-redirect">
                  <div className="auth-content">
                    <h2>Manager Access Required</h2>
                    <p>You need to authenticate as a manager to access this dashboard.</p>
                    <button 
                      onClick={() => window.location.href = 'https://calendar.joinoptivo.com'}
                      className="calendar-button"
                    >
                      Go to Calendar to Login
                    </button>
                  </div>
                </div>
              )
            }
          />
          <Route 
            path="*" 
            element={<Navigate to="/" replace />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App; 